import { useState } from 'react';
import {
  BLOCK_LABELS, blockKindOf, blockKinds, changeBlockKind, duplicateBlock, effectivePpi, hasOverride, layoutGridSpec, makeBlock, pageGeometry, pageInfo, photoPageKey, placeImage,
  removeBlock, renameBlock, reorderBlock, requiredPixels, resetOverride, resolveDesign, type BlockKind, type Element, type GridEl, type ImageEl, type ProjectDoc, type YearGridEl,
} from '@cal/engine';
import { Check, ColorField, Field, NumberField, Seg, Select, TextStyleEditor } from '../ui/controls';
import { edit, setProp, useEditor } from './store';
import { CropEditor } from './CropEditor';
import { addBlockKind } from './AddBlockMenu';
import { assetStore, thumbUrl } from '../assets';

const LIST_KINDS = [['public', 'Public holidays'], ['school', 'School holidays'], ['personal', 'Birthdays & anniversaries'], ['business', 'Business dates'], ['custom', 'Other important dates']] as const;
const TYPE_LABEL: Record<Element['type'], string> = { text: 'Text', image: 'Photograph frame', shape: 'Panel / shape', logo: 'Logo', grid: 'Calendar grid', mini: 'Mini month', legend: 'Event list (under the grid)', yeargrid: 'Year calendar (months at a glance)', key: 'Holiday key', holidays: 'Holiday & event list' };

export function Inspector({ doc, selected, pageLabel }: { doc: ProjectDoc; selected: string | null; pageLabel: string }) {
  const page = useEditor((s) => s.page);
  const scope = useEditor((s) => s.scope);
  const info = pageInfo(doc, page);
  const design = resolveDesign(doc, page);
  const el = design.elements.find((e) => e.id === selected);
  const isMonth = info.design === 'month';
  const overridden = el ? hasOverride(doc, page, el.id) : hasOverride(doc, page);

  return (
    <div>
      <div className="scope-bar">
        {isMonth ? (
          <>
            <div className="row">
              <span className="small muted grow">Apply changes to</span>
              <Seg label="Apply changes to" value={scope} onChange={(v) => useEditor.setState({ scope: v })}
                options={[{ value: 'page', label: `This month`, title: `Only ${pageLabel}` }, { value: 'all', label: 'All months', title: 'Every month page' }]} />
            </div>
            {overridden && (
              <div className="row small" style={{ color: '#6d28d9' }}>
                <span className="override-dot" />
                <span className="grow">{el ? `${pageLabel} has its own settings for this element` : `${pageLabel} has its own settings`}</span>
                <button className="btn sm" onClick={() => edit((d) => resetOverride(d, page, el?.id), 'reset')}>Reset to global</button>
              </div>
            )}
          </>
        ) : (
          <span className="small muted">Editing the {info.label.toLowerCase()} design (single page).</span>
        )}
      </div>
      {el ? <ElementSettings doc={doc} el={el} page={page} /> : <PageSettings doc={doc} />}
    </div>
  );
}

function PageSettings({ doc }: { doc: ProjectDoc }) {
  const page = useEditor((s) => s.page);
  const stage = useEditor((s) => s.stage);
  const scope = useEditor((s) => s.scope);
  const info = pageInfo(doc, page);
  const design = resolveDesign(doc, page);
  const setBackground = (c: ProjectDoc['designs']['month']['background']) =>
    edit((d) => {
      if (info.design !== 'month' || scope === 'all') {
        const overrides = Object.fromEntries(Object.entries(d.overrides).map(([k, o]) => { const x = { ...o }; delete x.background; return [k, x]; }));
        return { ...d, overrides: info.design === 'month' ? overrides : d.overrides, designs: { ...d.designs, [info.design]: { ...d.designs[info.design], background: c } } };
      }
      return { ...d, overrides: { ...d.overrides, [page]: { ...(d.overrides[page] ?? {}), background: c } } };
    }, 'page-bg');
  return (
    <div>
      <div className="section col">
        <h3>Page</h3>
        <ColorField label="Page background" value={design.background} allowNone onChange={setBackground} brandColors={doc.brand.colors} />
        <p className="small muted">Click an element on the page, or choose it below, to change it.</p>
      </div>
      {stage === 2 ? <><AddBlocks doc={doc} /><LayoutGridSettings doc={doc} /></> : (
        <div className="section col">
          <p className="small muted" style={{ margin: 0 }}>Blocks and the layout grid are set up in 2 · Design.</p>
          <button className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => useEditor.setState({ stage: 2 })}>Go to Design</button>
        </div>
      )}
      <div className="section">
        <h3>Elements on this page</h3>
        <div className="layers">
          {[...design.elements].reverse().map((e) => (
            <button key={e.id} aria-pressed={false} onClick={() => useEditor.setState({ selected: e.id })}>
              <span className="grow">{e.name}</span>
              {hasOverride(doc, page, e.id) && <span className="override-dot" title="Changed on this month" />}
              {e.hidden && <span className="badge">hidden</span>}
              <span className="faint small">{TYPE_LABEL[e.type]}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ElementSettings({ doc, el, page }: { doc: ProjectDoc; el: Element; page: string }) {
  const set = (path: string[], v: unknown) => setProp(el.id, path, v);
  const r = el.rect;
  return (
    <div>
      <div className="section col">
        <div className="row">
          <h2 className="grow" style={{ margin: 0 }}>{el.name}</h2>
          <button className="btn sm ghost" onClick={() => useEditor.setState({ selected: null })}>Done</button>
        </div>
        <span className="small faint">{TYPE_LABEL[el.type]}</span>
        <Field label="Block name"><input className="input" value={el.name} onChange={(e) => edit((d) => renameBlock(d, pageInfo(d, page).design, el.id, e.target.value), `name:${el.id}`)} /></Field>
        <BlockTypeField doc={doc} el={el} page={page} />
        <BlockActions doc={doc} el={el} page={page} />
      </div>
      {el.type === 'image' && <PhotoContent doc={doc} el={el} page={page} />}
      {el.type === 'text' && (
        <div className="section col">
          <h3>Text</h3>
          <Field label="Content" hint={<>Placeholders: {'{MONTH} {MONTH_SHORT} {YEAR} {CAPTION} {CONTACT} {CUSTOMER} {COMPANY} {TAGLINE} {PHONE} {EMAIL} {WEBSITE} {ADDRESS} {PERSON_NAME} {PERSON_TITLE}'} — fill them in Design → Branding</>}>
            <textarea className="input" rows={3} value={el.text} onChange={(e) => setProp(el.id, ['text'], e.target.value, `text:${el.id}`)} />
          </Field>
          <TextStyleEditor style={el.style} onChange={(p, v) => set(['style', ...p], v)} brandColors={doc.brand.colors} />
          <Field label="Vertical position in box">
            <Seg label="Vertical alignment" value={el.valign} onChange={(v) => set(['valign'], v)} options={[{ value: 'top', label: 'Top' }, { value: 'middle', label: 'Middle' }, { value: 'bottom', label: 'Bottom' }]} />
          </Field>
          <h3 style={{ marginTop: 8 }}>Legibility</h3>
          <div className="row wrap">
            <button className="btn sm" onClick={() => { set(['style', 'color'], { space: 'cmyk', c: 0, m: 0, y: 0, k: 100 }); set(['panel'], null); }}>Dark text</button>
            <button className="btn sm" onClick={() => set(['style', 'color'], { space: 'rgb', hex: '#ffffff' })}>Light text</button>
            <button className="btn sm" onClick={() => set(['panel'], el.panel ? null : { fill: { space: 'rgb', hex: '#000000' }, opacity: 0.4, radiusMm: 2, paddingMm: 2 })}>{el.panel ? 'Remove panel' : 'Add background panel'}</button>
          </div>
          {el.panel && (
            <div className="grid2">
              <ColorField label="Panel colour" value={el.panel.fill} onChange={(c) => c && set(['panel', 'fill'], c)} brandColors={doc.brand.colors} />
              <NumberField label="Panel opacity" unit="%" value={Math.round(el.panel.opacity * 100)} min={0} max={100} step={5} digits={0} onChange={(v) => set(['panel', 'opacity'], v / 100)} />
              <NumberField label="Corner radius" unit="mm" value={el.panel.radiusMm} min={0} max={100} onChange={(v) => set(['panel', 'radiusMm'], v)} />
              <NumberField label="Padding" unit="mm" value={el.panel.paddingMm} min={0} max={100} onChange={(v) => set(['panel', 'paddingMm'], v)} />
            </div>
          )}
        </div>
      )}
      {el.type === 'shape' && (
        <div className="section col">
          <h3>Panel</h3>
          <div className="grid2">
            <ColorField label="Fill" value={el.fill} allowNone onChange={(c) => set(['fill'], c)} brandColors={doc.brand.colors} />
            <NumberField label="Opacity" unit="%" value={Math.round(el.opacity * 100)} min={0} max={100} step={5} digits={0} onChange={(v) => set(['opacity'], v / 100)} />
            <NumberField label="Corner radius" unit="mm" value={el.radiusMm} min={0} max={200} onChange={(v) => set(['radiusMm'], v)} />
          </div>
          <StrokeEditor label="Border" value={el.stroke} onChange={(v) => set(['stroke'], v)} doc={doc} />
        </div>
      )}
      {el.type === 'logo' && (
        <div className="section col">
          <h3>Logo</h3>
          <p className="small muted">The logo comes from Design → Branding. Transparent PNGs keep their transparency.</p>
          <Field label="Alignment in box"><Seg label="Logo alignment" value={el.align} onChange={(v) => set(['align'], v)} options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' }]} /></Field>
        </div>
      )}
      {el.type === 'grid' && <GridSettings doc={doc} el={el} />}
      {el.type === 'yeargrid' && <YearSettings doc={doc} el={el} />}
      {el.type === 'holidays' && (
        <div className="section col" data-holiday-settings>
          <h3>Holiday & event list</h3>
          <p className="small muted">Lists the visible event sets of the kinds ticked below (Content → Holidays). Periods such as school holidays show as a date range. A public holiday falling on a Sunday is listed on its observed Monday with the marker.</p>
          <div className="col" style={{ gap: 2 }} role="group" aria-label="Show">
            {LIST_KINDS.map(([k, label]) => (
              <Check key={k} label={label} checked={el.kinds.includes(k)}
                onChange={(v) => { const next = v ? [...new Set([...el.kinds, k])] : el.kinds.filter((x) => x !== k); if (next.length) set(['kinds'], next); }} />
            ))}
          </div>
          <Field label="Layout"><Seg label="List layout" value={el.layout} onChange={(v) => set(['layout'], v)} options={[{ value: 'column', label: 'Portrait (one per line)' }, { value: 'row', label: 'Landscape (in a row)' }]} /></Field>
          {el.layout === 'column'
            ? <NumberField label="Columns" value={el.columns} min={1} max={6} step={1} digits={0} onChange={(v) => set(['columns'], Math.round(v))} />
            : <Field label="Separator"><input className="input" value={el.separator} maxLength={10} onChange={(e) => set(['separator'], e.target.value)} /></Field>}
          <Field label="Date format"><Seg label="Date format" value={el.dateFormat} onChange={(v) => set(['dateFormat'], v)} options={[{ value: 'numeric', label: '01/01' }, { value: 'short-month', label: '01 Jan' }]} /></Field>
          <Field label="Names"><Seg label="Holiday names" value={el.names} onChange={(v) => set(['names'], v)} options={[{ value: 'short', label: 'Short' }, { value: 'full', label: 'Full' }]} /></Field>
          <Field label="Holidays shown"><Seg label="Holidays shown" value={el.range} onChange={(v) => set(['range'], v)} options={[{ value: 'year', label: 'Whole year' }, { value: 'page', label: 'This page’s months' }]} /></Field>
          <Field label="Sunday holidays"><Seg label="Sunday holidays" value={el.observed} onChange={(v) => set(['observed'], v)} options={[{ value: 'marker', label: 'Monday only, marked' }, { value: 'both', label: 'Both days' }]} /></Field>
          <div className="grid2">
            <Field label="Marker"><input className="input" value={el.marker} maxLength={4} onChange={(e) => set(['marker'], e.target.value)} /></Field>
          </div>
          <Field label="Footnote (empty for none)"><input className="input" value={el.footnote} maxLength={200} onChange={(e) => set(['footnote'], e.target.value)} /></Field>
          <TextStyleEditor style={el.style} onChange={(p, v) => set(['style', ...p], v)} compact brandColors={doc.brand.colors} />
        </div>
      )}
      {el.type === 'key' && (
        <div className="section col">
          <h3>Holiday key</h3>
          <p className="small muted">Shows a swatch for each visible event set that shades days (Content → Holidays).</p>
          <Field label="Alignment"><Seg label="Key alignment" value={el.align} onChange={(v) => set(['align'], v)} options={[{ value: 'left', label: 'Left' }, { value: 'center', label: 'Centre' }, { value: 'right', label: 'Right' }]} /></Field>
          <TextStyleEditor style={el.style} onChange={(p, v) => set(['style', ...p], v)} compact brandColors={doc.brand.colors} />
        </div>
      )}
      {el.type === 'mini' && (
        <div className="section col">
          <h3>Mini month title</h3>
          <TextStyleEditor style={el.title} onChange={(p, v) => set(['title', ...p], v)} compact brandColors={doc.brand.colors} />
          <h3>Mini month dates</h3>
          <TextStyleEditor style={el.text} onChange={(p, v) => set(['text', ...p], v)} compact brandColors={doc.brand.colors} />
        </div>
      )}
      {el.type === 'legend' && (
        <div className="section col">
          <h3>Event list</h3>
          <p className="small muted">Lists events from sets with “List under the month grid” ticked (Content → Holidays).</p>
          <TextStyleEditor style={el.style} onChange={(p, v) => set(['style', ...p], v)} brandColors={doc.brand.colors} />
        </div>
      )}
      <div className="section col">
        <h3>Position & size</h3>
        <div className="grid2">
          <NumberField label="X (from left)" unit="mm" value={r.x} step={0.5} onChange={(v) => set(['rect'], { ...r, x: v })} />
          <NumberField label="Y (from top)" unit="mm" value={r.y} step={0.5} onChange={(v) => set(['rect'], { ...r, y: v })} />
          <NumberField label="Width" unit="mm" value={r.w} min={0.5} step={0.5} onChange={(v) => set(['rect'], { ...r, w: v })} />
          <NumberField label="Height" unit="mm" value={r.h} min={0.5} step={0.5} onChange={(v) => set(['rect'], { ...r, h: v })} />
        </div>
        <p className="small faint">Measured from the trimmed page edge. Drag on the page or use arrow keys to nudge; drag the handles to resize.{doc.layoutGrid.snap ? ' Snaps to the layout grid — hold Alt to place freely.' : ''}</p>
        {(el.type === 'image' || el.type === 'logo') && <RequiredSize doc={doc} el={el} page={page} />}
        <Check label="Hide this element" checked={!!el.hidden} onChange={(v) => set(['hidden'], v)} />
        {el.type === 'image' && <Check label="A photograph is required (blocks export when empty)" checked={el.required} onChange={(v) => set(['required'], v)} />}
        {el.type === 'image' && doc.product.productType === 'monthly' && <Check label="Same photograph on every month (e.g. agent portrait)" checked={!!el.shared} onChange={(v) => set(['shared'], v)} />}
      </div>
    </div>
  );
}

function StrokeEditor({ label, value, onChange, doc }: { label: string; value: { widthPt: number; color: import('@cal/engine').Color } | null; onChange: (v: unknown) => void; doc: ProjectDoc }) {
  return (
    <div className="grid2">
      <NumberField label={`${label} width`} unit="pt" value={value?.widthPt ?? 0} min={0} max={20} step={0.25}
        onChange={(w) => onChange(w <= 0 ? null : { widthPt: w, color: value?.color ?? { space: 'cmyk', c: 0, m: 0, y: 0, k: 100 } })} hint={value ? undefined : 'None'} />
      {value && <ColorField label={`${label} colour`} value={value.color} onChange={(c) => c && onChange({ ...value, color: c })} brandColors={doc.brand.colors} />}
    </div>
  );
}

function PhotoContent({ doc, el, page: pageKey }: { doc: ProjectDoc; el: ImageEl; page: string }) {
  const [crop, setCrop] = useState(false);
  const page = photoPageKey(pageKey, el);
  const placement = doc.photos[page]?.[String(el.slot)];
  const meta = placement ? assetStore.map[placement.assetId] : undefined;
  const set = (path: string[], v: unknown) => setProp(el.id, path, v);
  const updatePlacement = (patch: Partial<NonNullable<typeof placement>>, key = 'crop') =>
    edit((d) => ({ ...d, photos: { ...d.photos, [page]: { ...(d.photos[page] ?? {}), [el.slot]: { ...d.photos[page][String(el.slot)], ...patch } } } }), `${key}:${page}:${el.slot}`);
  const ppi = placement && meta ? (() => {
    const img = placeImage(el.rect, meta, placement);
    return Math.round(Math.min(effectivePpi(meta.width, img.w), effectivePpi(meta.height, img.h)));
  })() : null;
  return (
    <div className="section col">
      <h3>{el.shared ? 'Photograph (same on every page)' : `Photograph on ${pageInfo(doc, pageKey).label}`}</h3>
      {!placement ? (
        <div className="notice info">No photograph yet. Drag one from Content → Photographs onto this frame, or use Auto-assign.</div>
      ) : (
        <>
          <div className="row">
            <div style={{ width: 64, height: 64, borderRadius: 6, background: `#eee url(${thumbUrl(placement.assetId)}) center/cover` }} />
            <div className="col grow" style={{ gap: 2 }}>
              <span className="small" style={{ wordBreak: 'break-all' }}>{meta?.name ?? 'Loading…'}</span>
              {meta && <span className="small faint">{meta.width} × {meta.height} px</span>}
              {ppi !== null && <span className={`badge ${ppi >= doc.checks.ppiTarget ? 'ok' : ppi >= doc.checks.ppiMinimum ? 'warn' : 'err'}`}>{ppi} ppi at print size</span>}
            </div>
          </div>
          <Field label="Fit">
            <Seg label="Fit" value={placement.fit} onChange={(v) => updatePlacement({ fit: v, zoom: 1, panX: 0, panY: 0 })}
              options={[{ value: 'fill', label: 'Fill frame', title: 'Covers the frame; edges may be cropped' }, { value: 'fit', label: 'Fit whole photo', title: 'Shows the whole photo; may leave gaps' }]} />
          </Field>
          <div className="row wrap">
            <button className="btn sm primary" onClick={() => setCrop(true)}>Crop & position…</button>
            <button className="btn sm" onClick={() => updatePlacement({ zoom: 1, panX: 0, panY: 0 }, 'reset')}>Reset crop</button>
            <button className="btn sm danger" onClick={() => edit((d) => { const p = { ...(d.photos[page] ?? {}) }; delete p[String(el.slot)]; return { ...d, photos: { ...d.photos, [page]: p } }; }, 'remove')}>Remove</button>
          </div>
        </>
      )}
      <ReplaceFromPool doc={doc} onPick={(id) => edit((d) => ({ ...d, photos: { ...d.photos, [page]: { ...(d.photos[page] ?? {}), [el.slot]: { assetId: id, fit: 'fill', zoom: 1, panX: 0, panY: 0 } } } }), 'replace')} current={placement?.assetId} />
      <h3 style={{ marginTop: 8 }}>Frame</h3>
      <div className="grid2">
        <NumberField label="Corner radius" unit="mm" value={el.radiusMm} min={0} max={200} onChange={(v) => set(['radiusMm'], v)} />
      </div>
      <StrokeEditor label="Border" value={el.border} onChange={(v) => set(['border'], v)} doc={doc} />
      {crop && placement && meta && <CropEditor frame={el.rect} radius={el.radiusMm} meta={meta} placement={placement} onChange={(p) => updatePlacement(p)} onClose={() => setCrop(false)} />}
    </div>
  );
}

function ReplaceFromPool({ doc, onPick, current }: { doc: ProjectDoc; onPick: (id: string) => void; current?: string }) {
  const [open, setOpen] = useState(false);
  if (!doc.photoPool.length) return null;
  return (
    <details className="adv" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary>{current ? 'Replace with another photograph' : 'Choose a photograph'}</summary>
      <div className="pool">
        {doc.photoPool.map((id) => (
          <button key={id} className="pool-item" aria-pressed={id === current} style={{ backgroundImage: `url(${thumbUrl(id)})` }} aria-label={`Use ${assetStore.map[id]?.name ?? 'photo'}`} onClick={() => onPick(id)} />
        ))}
      </div>
    </details>
  );
}

function GridSettings({ doc, el }: { doc: ProjectDoc; el: GridEl }) {
  const g = el.style;
  const set = (path: string[], v: unknown) => setProp(el.id, ['style', ...path], v);
  const bc = doc.brand.colors;
  return (
    <>
      <div className="section col">
        <h3>Lines & cells</h3>
        <StrokeEditor label="Outer border" value={g.outerBorder} onChange={(v) => set(['outerBorder'], v)} doc={doc} />
        <StrokeEditor label="Inner lines" value={g.innerLines} onChange={(v) => set(['innerLines'], v)} doc={doc} />
        <div className="grid2">
          <ColorField label="Day background" value={g.cellFill} allowNone onChange={(c) => set(['cellFill'], c)} brandColors={bc} />
          <NumberField label="Cell padding" unit="mm" value={g.cellPaddingMm} min={0} max={20} step={0.25} onChange={(v) => set(['cellPaddingMm'], v)} />
        </div>
        <Select label="Date position" value={g.datePosition} onChange={(v) => set(['datePosition'], v)}
          options={[{ value: 'top-left', label: 'Top left' }, { value: 'top-center', label: 'Top centre' }, { value: 'top-right', label: 'Top right' }, { value: 'center', label: 'Centre' }, { value: 'bottom-right', label: 'Bottom right' }]} />
      </div>
      <div className="section col">
        <h3>Weekends</h3>
        <div className="grid2">
          <ColorField label="Saturday background" value={g.saturday.fill} allowNone onChange={(c) => set(['saturday', 'fill'], c)} brandColors={bc} />
          <ColorField label="Saturday date colour" value={g.saturday.textColor} allowNone onChange={(c) => set(['saturday', 'textColor'], c)} brandColors={bc} />
          <ColorField label="Sunday background" value={g.sunday.fill} allowNone onChange={(c) => set(['sunday', 'fill'], c)} brandColors={bc} />
          <ColorField label="Sunday date colour" value={g.sunday.textColor} allowNone onChange={(c) => set(['sunday', 'textColor'], c)} brandColors={bc} />
        </div>
      </div>
      <div className="section col">
        <h3>Weekday header</h3>
        <div className="grid2">
          <NumberField label="Header height" unit="mm" value={g.header.heightMm} min={0} max={100} step={0.5} onChange={(v) => set(['header', 'heightMm'], v)} />
          <ColorField label="Header background" value={g.header.fill} allowNone onChange={(c) => set(['header', 'fill'], c)} brandColors={bc} />
        </div>
        <Select label="Labels" value={g.header.labels} onChange={(v) => set(['header', 'labels'], v)} options={[{ value: 'short', label: 'Mon Tue Wed' }, { value: 'narrow', label: 'M T W' }, { value: 'full', label: 'Monday Tuesday' }]} />
        <TextStyleEditor style={g.header.text} onChange={(p, v) => set(['header', 'text', ...p], v)} compact brandColors={bc} />
      </div>
      <div className="section col">
        <h3>Dates</h3>
        <TextStyleEditor style={g.dateText} onChange={(p, v) => set(['dateText', ...p], v)} compact brandColors={bc} />
      </div>
      <div className="section col">
        <details className="adv">
          <summary>Advanced: event labels, adjacent-month dates, week numbers</summary>
          <div className="col">
            <h3>Event labels in day cells</h3>
            <TextStyleEditor style={g.eventText} onChange={(p, v) => set(['eventText', ...p], v)} compact brandColors={bc} />
            <p className="small faint">Label colours come from each event set (Content → Holidays); this sets font and size.</p>
            <h3>Adjacent-month dates</h3>
            <TextStyleEditor style={g.adjacentText} onChange={(p, v) => set(['adjacentText', ...p], v)} compact brandColors={bc} />
            <h3>Week numbers</h3>
            <NumberField label="Column width" unit="mm" value={g.weekNumbers.widthMm} min={3} max={40} onChange={(v) => set(['weekNumbers', 'widthMm'], v)} />
            <TextStyleEditor style={g.weekNumbers.text} onChange={(p, v) => set(['weekNumbers', 'text', ...p], v)} compact brandColors={bc} />
          </div>
        </details>
      </div>
    </>
  );
}

function YearSettings({ doc, el }: { doc: ProjectDoc; el: YearGridEl }) {
  const y = el.style;
  const set = (path: string[], v: unknown) => setProp(el.id, path, v);
  const bc = doc.brand.colors;
  return (
    <>
      <div className="section col">
        <h3>Layout</h3>
        <Select label="Months per row" value={el.columns} onChange={(v) => set(['columns'], v)} options={[1, 2, 3, 4, 6, 12].map((n) => ({ value: n, label: `${n} across × ${Math.ceil(12 / n)} down` }))} />
        <div className="grid2">
          <NumberField label="Gap across" unit="mm" value={el.gapXmm} min={0} max={200} step={0.5} onChange={(v) => set(['gapXmm'], v)} />
          <NumberField label="Gap down" unit="mm" value={el.gapYmm} min={0} max={200} step={0.5} onChange={(v) => set(['gapYmm'], v)} />
        </div>
      </div>
      <div className="section col">
        <h3>Month names</h3>
        <div className="grid2">
          <ColorField label="Bar colour" value={y.titleFill} allowNone onChange={(c) => set(['style', 'titleFill'], c)} brandColors={bc} />
          <NumberField label="Bar height" unit="mm" value={y.titleHeightMm} min={0} max={200} step={0.25} onChange={(v) => set(['style', 'titleHeightMm'], v)} />
          <NumberField label="Bar rounding" unit="mm" value={y.titleRadiusMm} min={0} max={50} step={0.25} onChange={(v) => set(['style', 'titleRadiusMm'], v)} />
        </div>
        <TextStyleEditor style={y.title} onChange={(p, v) => set(['style', 'title', ...p], v)} compact brandColors={bc} />
      </div>
      <div className="section col">
        <h3>Weekday letters</h3>
        <Select label="Labels" value={y.weekdayLabels} onChange={(v) => set(['style', 'weekdayLabels'], v)} options={[{ value: 'narrow', label: 'M T W' }, { value: 'short', label: 'Mon Tue Wed' }]} />
        <TextStyleEditor style={y.weekday} onChange={(p, v) => set(['style', 'weekday', ...p], v)} compact brandColors={bc} />
      </div>
      <div className="section col">
        <h3>Dates</h3>
        <TextStyleEditor style={y.date} onChange={(p, v) => set(['style', 'date', ...p], v)} compact brandColors={bc} />
        <div className="grid2">
          <ColorField label="Saturday dates" value={y.saturdayColor} allowNone onChange={(c) => set(['style', 'saturdayColor'], c)} brandColors={bc} />
          <ColorField label="Sunday dates" value={y.sundayColor} allowNone onChange={(c) => set(['style', 'sundayColor'], c)} brandColors={bc} />
          <ColorField label="Weekend shading" value={y.weekendFill} allowNone onChange={(c) => set(['style', 'weekendFill'], c)} brandColors={bc} />
          <NumberField label="Shading rounding" unit="mm" value={y.shadeRadiusMm} min={0} max={20} step={0.1} onChange={(v) => set(['style', 'shadeRadiusMm'], v)} />
        </div>
        <p className="small faint">Holiday shading and colours come from each event set (Content → Holidays).</p>
      </div>
    </>
  );
}

/** "This frame needs …": size in mm (incl. bleed where the frame runs off the page) and pixels at the target resolution. */
function RequiredSize({ doc, el, page }: { doc: ProjectDoc; el: Element; page: string }) {
  const g = pageGeometry(doc, page);
  const r = el.rect;
  // frames touching the trim edge run into the bleed, so the image must cover that too
  const bx = (r.x <= 0.5 ? g.bleed : 0) + (r.x + r.w >= g.trimW - 0.5 ? g.bleed : 0);
  const by = (r.y <= 0.5 ? g.bleed : 0) + (r.y + r.h >= g.trimH - 0.5 ? g.bleed : 0);
  const size = { w: r.w + (el.type === 'image' ? bx : 0), h: r.h + (el.type === 'image' ? by : 0) };
  const ppi = doc.checks.ppiTarget;
  const px = requiredPixels(size, ppi);
  return (
    <div className="notice info small" data-required-size>
      {el.type === 'logo' ? 'Logo area' : 'Image needed'}: <strong>{+size.w.toFixed(1)} × {+size.h.toFixed(1)} mm</strong> = <strong>{px.w} × {px.h} px</strong> at {ppi} ppi
      {el.type === 'logo' ? ' (or a vector-quality PNG with transparency; the logo is fitted inside).' : '. Larger photos are fine; smaller ones are flagged in Review.'}
    </div>
  );
}

/** Delete, duplicate and stacking order of the selected block. */
function BlockActions({ doc, el, page }: { doc: ProjectDoc; el: Element; page: string }) {
  const design = pageInfo(doc, page).design;
  const spec = layoutGridSpec(doc, page);
  return (
    <div className="row wrap" style={{ gap: 6 }}>
      <button className="btn sm" onClick={() => {
        const off = doc.layoutGrid.snap ? { x: 0, y: spec.cellH + spec.gutter } : { x: 5, y: 5 };
        const r = duplicateBlock(doc, design, el.id, off);
        edit(() => r.doc, `dup:${el.id}`);
        useEditor.setState({ selected: r.id });
      }}>Duplicate</button>
      <button className="btn sm" title="Bring to front" onClick={() => edit((d) => reorderBlock(d, design, el.id, 'front'), `order:${el.id}`)}>To front</button>
      <button className="btn sm" title="Send to back" onClick={() => edit((d) => reorderBlock(d, design, el.id, 'back'), `order:${el.id}`)}>To back</button>
      <button className="btn sm danger" title="Delete block (Delete key)" onClick={() => { edit((d) => removeBlock(d, design, el.id), `del:${el.id}`); useEditor.setState({ selected: null }); }}>Delete block</button>
    </div>
  );
}

/** Add image / logo / text / panel blocks; new blocks land on the layout grid. */
function AddBlocks({ doc }: { doc: ProjectDoc }) {
  const page = useEditor((s) => s.page);
  const info = pageInfo(doc, page);
  const add = (kind: BlockKind) => addBlockKind(doc, page, kind);
  return (
    <div className="section col">
      <h3>Add a block</h3>
      <div className="row wrap" style={{ gap: 6 }} role="group" aria-label="Add a block">
        {blockKinds(info.design).map((k) => <button key={k} className="btn sm" onClick={() => add(k)}>+ {BLOCK_LABELS[k]}</button>)}
      </div>
      <p className="small faint" style={{ margin: 0 }}>
        {info.design === 'month'
          ? 'Blocks appear on every month. Each month can have its own photo in an image block (or tick “same photograph on every month”); hide a block on one month with “This month” + Hide.'
          : info.design === 'face' ? 'Blocks appear on both faces; each face can have its own photo.' : 'Select a block to drop a photo or logo into it, edit text or resize it.'}
      </p>
    </div>
  );
}

/** Columns × rows the blocks snap to, inside the safe area and clear of binding / rim zones. */
function LayoutGridSettings({ doc }: { doc: ProjectDoc }) {
  const g = doc.layoutGrid;
  const set = (patch: Partial<typeof g>) => edit((d) => ({ ...d, layoutGrid: { ...d.layoutGrid, ...patch } }), `grid:${Object.keys(patch).join(',')}`);
  return (
    <div className="section col">
      <h3>Layout grid</h3>
      <div className="grid2">
        <NumberField label="Columns" value={g.cols} min={1} max={48} step={1} digits={0} onChange={(v) => set({ cols: Math.round(v) })} />
        <NumberField label="Rows" value={g.rows} min={1} max={48} step={1} digits={0} onChange={(v) => set({ rows: Math.round(v) })} />
        <NumberField label="Gap between cells" unit="mm" value={g.gutterMm} min={0} max={50} step={0.5} onChange={(v) => set({ gutterMm: v })} />
      </div>
      <Check label="Show grid" checked={g.show} onChange={(v) => set({ show: v })} />
      <Check label="Snap to grid (hold Alt to place freely)" checked={g.snap} onChange={(v) => set({ snap: v })} />
      <p className="small faint" style={{ margin: 0 }}>More columns and rows give finer steps; fewer give bigger cells for quick alignment.</p>
    </div>
  );
}

/** Switch a block between text, image, logo, panel …, keeping its size and position. */
function BlockTypeField({ doc, el, page }: { doc: ProjectDoc; el: Element; page: string }) {
  const kind = blockKindOf(el);
  if (!kind) return null; // the calendar itself
  const design = pageInfo(doc, page).design;
  const kinds = blockKinds(design);
  return (
    <Select label="Block type" value={kind} options={[...new Set([...kinds, kind])].map((k) => ({ value: k, label: BLOCK_LABELS[k] }))}
      hint="Changing the type keeps the size and position; the old content (text, photos) is removed. Undo with Ctrl+Z."
      onChange={(k) => {
        const r = changeBlockKind(doc, design, el.id, k);
        edit(() => r.doc, `kind:${el.id}`);
        useEditor.setState({ selected: r.id });
      }} />
  );
}
